import FormatHelper from "@/helpers/format.helper.js";

class MissionFormatter {

    extractScheduledTimeParts(scheduledTime) {
        if (scheduledTime == null) {
            return {
                scheduledHour: "",
                scheduledMinute: "",
            };
        }

        const raw = String(scheduledTime).trim();
        if (!raw) {
            return {
                scheduledHour: "",
                scheduledMinute: "",
            };
        }

        const [hourRaw = "", minuteRaw = ""] = raw.split(":");
        const hour = Number(hourRaw);
        const minute = Number(minuteRaw);

        if (!Number.isInteger(hour) || hour < 0 || hour > 23 || !Number.isInteger(minute) || minute < 0 || minute > 59) {
            return {
                scheduledHour: "",
                scheduledMinute: "",
            };
        }

        return {
            scheduledHour: hour.toString().padStart(2, "0"),
            scheduledMinute: minute.toString().padStart(2, "0"),
        };
    }

    formatScheduledTime(scheduledTime) {
        if (scheduledTime == null) {
            return {
                displayTime: null,
                displayTimeSortValue: null,
            };
        }

        const raw = String(scheduledTime).trim();
        if (!raw) {
            return {
                displayTime: null,
                displayTimeSortValue: null,
            };
        }

        const [hourRaw = "", minuteRaw = ""] = raw.split(":");
        const hour = Number(hourRaw);
        const minute = Number(minuteRaw);

        if (!Number.isInteger(hour) || hour < 0 || hour > 23 || !Number.isInteger(minute) || minute < 0 || minute > 59) {
            return {
                displayTime: null,
                displayTimeSortValue: null,
            };
        }

        return {
            displayTime: `${hour.toString().padStart(2, "0")}h${minute.toString().padStart(2, "0")}`,
            displayTimeSortValue: (hour * 60) + minute,
        };
    }

    extractMissionNameParts(name) {
        const rawName = name == null ? "Aucune donnée" : String(name);
        const normalizedName = rawName.trim().replace(/\s+/g, " ");
        
        return {
            name: rawName,
            displayName: normalizedName || rawName,
        };
    }

    missionFormat(data) {
        if (!data) return null;

        const padWithZero = (number) => number.toString().padStart(2, '0');

        let duration = "";
        if (data.startDate && data.endDate) {
            const start = new Date(data.startDate);
            const end = new Date(data.endDate);
            if (!isNaN(start) && !isNaN(end) && end >= start) {
                const diffSec = Math.floor((end - start) / 1000);
                const h = Math.floor(diffSec / 3600);
                const m = Math.floor((diffSec % 3600) / 60);
                duration = `${h}h${padWithZero(m)}`;
            }
        }

        const { name, displayName } = this.extractMissionNameParts(data.name);
        const { scheduledHour, scheduledMinute } = this.extractScheduledTimeParts(data.scheduledTime);
        const { displayTime, displayTimeSortValue } = this.formatScheduledTime(data.scheduledTime);
        const transporteur = data.transporteur ? FormatHelper.baliseInfoFormat(data.transporteur) : null;
        const corridoring = FormatHelper.corridoringInfoFormat(data.corridoring || null);
        const escorte = Array.isArray(data.escorte) ? FormatHelper.balisesResponseFormat(data.escorte) : [];
        const members = Array.isArray(data.missionMembers) ? FormatHelper.missionMembersFormat(data.missionMembers) : [];
        const missionPlates = Array.isArray(data.missionPlates) ? data.missionPlates : [];
        const distance = (typeof data.distance === 'number' && isFinite(data.distance) && data.distance <= 2_000_000)
            ? Math.round((data.distance / 1000) * 10) / 10
            : null;

        return {
            id: data.id,
            name,
            displayName,
            displayTime,
            displayTimeSortValue,
            scheduledTime: data.scheduledTime ?? null,
            scheduledHour,
            scheduledMinute,
            withoutCorridoring: data.withoutCorridoring,
            status: data.status,
            startAddress: data.startAddress ?? "",
            endAddress: data.endAddress ?? "",
            startLatitude: data.startLatitude ?? null,
            startLongitude: data.startLongitude ?? null,
            endLatitude: data.endLatitude ?? null,
            endLongitude: data.endLongitude ?? null,
            startDate: data.startDate ?? null,
            endDate: data.endDate ?? null,
            displayStartDate: FormatHelper.formatDate(data.startDate) ?? "",
            displayEndDate: FormatHelper.formatDate(data.endDate) ?? "",
            corridoring,
            balises: escorte,
            members,
            transporteur,
            transporteurId: data.transporteurId ?? null,
            duration,
            distance,
            missionPlates,
            missionPrecheckValues: Array.isArray(data.missionPrecheckValues) ? data.missionPrecheckValues : [],
            driverVerified: data.driverVerified || false,
            verifiedInfo: data.verifiedInfo || "NONE",
        };
    }

    missionsFormat(data) {
        if (!Array.isArray(data)) return [];
        return data.map(mission => this.missionFormat(mission));
    }

    missionsSearchFormat(data, fallbackPage = 1, fallbackPerPage = 15) {
        const rows = data && Array.isArray(data.rows)
            ? data.rows
            : Array.isArray(data)
                ? data
                : [];

        const count = data && data.count !== undefined && data.count !== null
            ? data.count
            : rows.length;

        const page = Number(data?.page) || fallbackPage;
        const perPage = Number(data?.perPage) || fallbackPerPage;
        const totalPages = Number(data?.totalPages) || Math.ceil(count / perPage) || 1;

        return {
            rows: this.missionsFormat(rows),
            count,
            page,
            perPage,
            totalPages
        };
    }
}

export default new MissionFormatter();